package terraultima

import kotlin.math.abs
import kotlin.math.pow
import kotlin.math.roundToLong

/**
 * Number formatting the engine can use on any platform.
 *
 * `String.format` is a JVM API. It does not exist in Kotlin/JS or Kotlin/Wasm, and the engine used
 * it in nine places — which was the only thing standing between this simulation and running inside
 * a browser (see `docs/11-TELEGRAM.md` §4). These are deliberately the *only* three shapes the
 * engine needs; a frontend that wants thousands separators or locale-aware output does that in its
 * own presentation layer, where a platform API is fine.
 *
 * Kept in `terraultima` rather than a `util` package because it is engine vocabulary: every number
 * the player is shown passes through here or through a frontend's own formatter.
 */
object Fmt {

    /** `dec(2.5, 1)` -> "2.5". Half-up, like `%.1f`, and without a locale's comma. */
    fun dec(v: Double, places: Int): String {
        if (v.isNaN()) return "NaN"
        if (v.isInfinite()) return if (v > 0) "Inf" else "-Inf"
        if (places <= 0) return v.roundToLong().toString()
        val scale = 10.0.pow(places)
        val scaled = (abs(v) * scale).roundToLong()
        val whole = scaled / scale.toLong()
        val frac = (scaled % scale.toLong()).toString().padStart(places, '0')
        return (if (v < 0 && (whole != 0L || frac.any { it != '0' })) "-" else "") + "$whole.$frac"
    }

    /** `%.0f`. */
    fun int(v: Double): String = v.roundToLong().toString()

    /**
     * Two significant figures — the `%.2g` this replaces, minus its exponent form.
     *
     * Only ever applied to the small numbers a debug readout shows (`abs(x) < 10` at the one call
     * site), so scientific notation was never actually reachable and is not reproduced.
     */
    fun sig2(v: Double): String {
        if (v == 0.0) return "0.0"
        val a = abs(v)
        val places = when {
            a >= 10 -> 0
            a >= 1 -> 1
            a >= 0.1 -> 2
            a >= 0.01 -> 3
            else -> 4
        }
        return dec(v, places)
    }
}
